import Button from '@/components/global/button';
import ROUTES from '@/lib/constants/routeConfig';
import { useAuth } from '@/lib/hooks/useAuth';
import { useNotifications } from '@/lib/hooks/useNotifications';
import { useUser } from '@/lib/hooks/useUser';
import { conversationsActions } from '@/lib/store/conversations.store';
import { useI18n } from '@/locales/client';
import { Bell, MessageCircle, X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
export function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    groupedNotifications,
    isLoading,
    hasUnread,
    markAllAsRead,
    fetchNotifications,
  } = useNotifications();
  const router = useRouter();
  const { isLawyer } = useUser();
  const { session } = useAuth();
  const t = useI18n();

  const handleToggle = async () => {
    if (!isOpen) {
      await fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    setIsOpen(false);
  };

  const handleNotificationClick = async (conversationId: string) => {
    // Close dropdown
    setIsOpen(false);

    // Force refresh conversations and set active conversation before navigating
    if (session) {
      // Fetch latest conversations to ensure we have the most recent data
      await conversationsActions.fetchConversations(session);
      // Set the specific conversation as active
      await conversationsActions.setActiveConversationById(
        conversationId,
        session
      );
    }

    // Navigate to the specific conversation
    const conversationsRoute = isLawyer
      ? ROUTES.DASHBOARD.CONVERSATIONS
      : ROUTES.MARKETPLACE.CONVERSATIONS;

    router.push(`${conversationsRoute}?chatId=${conversationId}`);
  };

  const formatTime = (dateString: string) => {
    try {
      const messageDate = new Date(dateString);
      const now = new Date();
      const diffInMinutes = Math.floor(
        (now.getTime() - messageDate.getTime()) / (1000 * 60)
      );

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;

      const diffInDays = Math.floor(diffInMinutes / 1440);
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 7) return `${diffInDays}d`;

      return messageDate.toLocaleDateString();
    } catch {
      return 'Unknown time';
    }
  };

  return (
    <div className='relative'>
      {/* Notification Button */}
      <div className='relative'>
        <Button
          variant='ghost'
          size='icon'
          className='h-10 w-10'
          onClick={handleToggle}
        >
          <Bell className='h-5 w-5' />
        </Button>

        {/* Notification Badge */}
        {hasUnread && (
          <div className='pointer-events-none absolute right-[8px] top-[10px]'>
            <svg
              width='15'
              height='17'
              viewBox='0 0 15 17'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g filter='url(#filter0_d_1_21530)'>
                <circle cx='8.5' cy='6.5' r='2.5' fill='#FF7979' />
              </g>
              <defs>
                <filter
                  id='filter0_d_1_21530'
                  x='0.8'
                  y='0.8'
                  width='15.4'
                  height='15.4'
                  filterUnits='userSpaceOnUse'
                  colorInterpolationFilters='sRGB'
                >
                  <feFlood floodOpacity='0' result='BackgroundImageFix' />
                  <feColorMatrix
                    in='SourceAlpha'
                    type='matrix'
                    values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                    result='hardAlpha'
                  />
                  <feOffset dy='2' />
                  <feGaussianBlur stdDeviation='2.6' />
                  <feComposite in2='hardAlpha' operator='out' />
                  <feColorMatrix
                    type='matrix'
                    values='0 0 0 0 1 0 0 0 0 0.47451 0 0 0 0 0.47451 0 0 0 1 0'
                  />
                  <feBlend
                    mode='normal'
                    in2='BackgroundImageFix'
                    result='effect1_dropShadow_1_21530'
                  />
                  <feBlend
                    mode='normal'
                    in='SourceGraphic'
                    in2='effect1_dropShadow_1_21530'
                    result='shape'
                  />
                </filter>
              </defs>
            </svg>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className='absolute right-0 top-12 z-50 w-80 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800'>
          {/* Header */}
          <div className='flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700'>
            <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
              {t('dashboard.notifications.title')}
            </h3>
            <div className='flex items-center gap-2'>
              {hasUnread && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleMarkAllAsRead}
                  className='text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                >
                  {t('dashboard.notifications.markAllAsRead')}
                </Button>
              )}
              <Button
                variant='ghost'
                size='icon'
                onClick={() => setIsOpen(false)}
                className='h-6 w-6'
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className='max-h-96 overflow-y-auto'>
            {isLoading ? (
              <div className='flex items-center justify-center p-8'>
                <div className='text-sm text-gray-500 dark:text-gray-400'>
                  {t('dashboard.notifications.loading')}
                </div>
              </div>
            ) : !groupedNotifications?.length ? (
              <div className='flex flex-col items-center justify-center p-8 text-center'>
                <Bell className='mb-2 h-8 w-8 text-gray-400' />
                <div className='text-sm text-gray-500 dark:text-gray-400'>
                  {t('dashboard.notifications.noNotifications')}
                </div>
              </div>
            ) : (
              <div className='divide-y divide-gray-100 dark:divide-gray-700'>
                {groupedNotifications.map((notification, index) => (
                  <div
                    key={notification.conversationId}
                    className='flex cursor-pointer items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    onClick={() =>
                      handleNotificationClick(notification.conversationId)
                    }
                  >
                    {/* Icon */}
                    <div className='relative flex-shrink-0'>
                      <div
                        className='flex h-10 w-10 items-center justify-center rounded-full'
                        style={{ backgroundColor: '#9E7BFF' }}
                      >
                        <MessageCircle className='h-5 w-5 text-white' />
                      </div>
                      {/* Badge com quantidade de mensagens */}
                      <div className='absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border-[1.5px] border-white bg-red-500'>
                        <span className='text-xs font-semibold text-white'>
                          {notification.count}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className='min-w-0 flex-1'>
                      <div className='flex items-center justify-between'>
                        <p className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                          {notification.senderName}{' '}
                          {notification.count === 1
                            ? t('dashboard.notifications.sentYouMessage')
                            : (t as any)(
                                'dashboard.notifications.sentYouMessages',
                                {
                                  count: notification.count,
                                }
                              )}
                        </p>
                        <span className='text-xs text-gray-500 dark:text-gray-400'>
                          {formatTime(notification.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overlay */}
      {isOpen && (
        <div className='fixed inset-0 z-40' onClick={() => setIsOpen(false)} />
      )}
    </div>
  );
}
